/**
 * Falling-ASCII-snowflakes engine — a tiny framework-free canvas renderer.
 *
 * Kept dependency-free (no Vue/Nuxt imports) so it can be reused outside
 * front-office (see the dev-only DialKit playground) and unit-tested in a
 * plain jsdom environment.
 *
 * Behavior (per the Figma "Festive FAB" dev annotation):
 * - General wind direction left-to-right
 * - Randomized drift, rotation, size, and opacity multipliers per flake
 * - `hoverSlowdown` factor (0.5 = half speed) applied while hovered
 * - Respects `prefers-reduced-motion`: renders one static scattered frame
 *   instead of animating (and re-evaluates the media query live)
 * - Pauses the rAF loop while the tab is hidden
 */

export interface SnowfallOptions {
  /** Characters to draw, picked randomly per flake. */
  characters: string[]
  /** Flakes per 1000 px² of canvas. */
  density: number
  /** Base vertical fall speed, px/s. */
  fallSpeed: number
  /** Base horizontal wind (positive = LTR), px/s. */
  wind: number
  /** Horizontal drift/sway amplitude, px. */
  drift: number
  /** Drift/sway frequency, rad/s. */
  driftFrequency: number
  /** Base rotation speed, deg/s. */
  rotationSpeed: number
  /** Base glyph font size, px (per-flake size multiplier applied on top). */
  fontSize: number
  /** CSS font stack for the glyphs. */
  fontFamily: string
  /** Glyph fill color. */
  color: string
  /** Base glyph opacity (per-flake multiplier applied on top). */
  opacity: number
  /** Speed multiplier while hovered. Default 1 (no slowdown). */
  hoverSlowdown: number
}

export const DEFAULT_SNOWFALL_OPTIONS: SnowfallOptions = {
  characters: ['•', '*', '+'],
  density: 1.6,
  fallSpeed: 14,
  wind: 6,
  drift: 14,
  driftFrequency: 1.2,
  rotationSpeed: 40,
  fontSize: 16,
  fontFamily: '"BM Duplet TXT", "Helvetica TXT", sans-serif',
  color: '#ffffff',
  opacity: 0.9,
  hoverSlowdown: 1,
}

const PER_FLAKE_SIZE_RANGE = 0.75
const PER_FLAKE_OPACITY_RANGE = 0.5

export interface Flake {
  character: string
  x: number
  y: number
  sizeMultiplier: number
  opacityMultiplier: number
  speedMultiplier: number
  driftMultiplier: number
  phase: number
  rotation: number
  rotationSpeedMultiplier: number
}

export class SnowfallEngine {
  private canvas: HTMLCanvasElement
  private ctx: CanvasRenderingContext2D | null
  private options: SnowfallOptions
  private flakes: Flake[] = []
  private rafId: number | null = null
  private lastFrameTime: number | null = null
  private hovered = false
  private reducedMotionQuery: MediaQueryList | null = null
  private resizeObserver: ResizeObserver | null = null
  private visibilityListener: () => void
  private resizeListener: () => void

  constructor(
    canvas: HTMLCanvasElement,
    options: Partial<SnowfallOptions> = {},
  ) {
    this.canvas = canvas
    this.options = { ...DEFAULT_SNOWFALL_OPTIONS, ...options }
    this.ctx = canvas.getContext('2d')

    if (typeof window !== 'undefined' && window.matchMedia) {
      this.reducedMotionQuery = window.matchMedia(
        '(prefers-reduced-motion: reduce)',
      )
      this.reducedMotionQuery.addEventListener?.('change', this.handleChange)
    }

    this.visibilityListener = () => {
      if (document.hidden) {
        this.stop()
      } else {
        this.start()
      }
    }
    document.addEventListener('visibilitychange', this.visibilityListener)

    this.resizeListener = () => {
      this.syncSize()
      this.populateFlakes()
      this.renderStaticFrame()
    }
    window.addEventListener('resize', this.resizeListener)

    // Keep the canvas in step with the host element's own size changes
    // (e.g. the FAB shifting above a sticky bar) — window `resize` alone
    // misses those. jsdom-safe: only observed when available.
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this.syncSize()
        this.populateFlakes()
        this.renderStaticFrame()
      })
      this.resizeObserver.observe(canvas)
    }

    this.syncSize()
    this.populateFlakes()
  }

  setOptions(options: Partial<SnowfallOptions>) {
    this.options = { ...this.options, ...options }
    this.populateFlakes()

    if (this.isReducedMotion()) {
      this.stop()
      this.renderStaticFrame()
    }
  }

  setHovered(hovered: boolean) {
    this.hovered = hovered
  }

  start() {
    if (this.rafId !== null) return
    if (this.isReducedMotion()) {
      this.renderStaticFrame()
      return
    }

    this.lastFrameTime = null
    const tick = (time: number) => {
      const delta = this.lastFrameTime === null ? 16 : time - this.lastFrameTime
      this.lastFrameTime = time
      this.step(Math.min(delta, 100) / 1000)
      this.render()
      this.rafId = requestAnimationFrame(tick)
    }
    this.rafId = requestAnimationFrame(tick)
  }

  stop() {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId)
      this.rafId = null
    }
  }

  destroy() {
    this.stop()
    this.reducedMotionQuery?.removeEventListener?.('change', this.handleChange)
    this.resizeObserver?.disconnect()
    document.removeEventListener('visibilitychange', this.visibilityListener)
    window.removeEventListener('resize', this.resizeListener)
  }

  isReducedMotion() {
    return this.reducedMotionQuery?.matches === true
  }

  /** Current flakes — exposed for tests and dev tooling (DialKit). */
  getFlakes(): readonly Flake[] {
    return this.flakes
  }

  private handleChange = () => {
    if (this.isReducedMotion()) {
      this.stop()
      this.renderStaticFrame()
    } else {
      this.start()
    }
  }

  private get cssSize() {
    return {
      width: this.canvas.clientWidth,
      height: this.canvas.clientHeight,
    }
  }

  private syncSize() {
    const dpr = window.devicePixelRatio || 1
    const { width, height } = this.cssSize

    if (
      this.canvas.width !== width * dpr ||
      this.canvas.height !== height * dpr
    ) {
      this.canvas.width = Math.max(1, Math.round(width * dpr))
      this.canvas.height = Math.max(1, Math.round(height * dpr))
    }

    const { ctx } = this
    if (ctx) {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }
  }

  private populateFlakes() {
    const { width, height } = this.cssSize
    const area = width * height
    const count = Math.max(1, Math.round((area / 1000) * this.options.density))

    this.flakes = Array.from({ length: count }, () => this.createFlake(true))
  }

  private createFlake(anywhere: boolean): Flake {
    const { width, height } = this.cssSize
    const { random } = Math

    return {
      character:
        this.options.characters[
          Math.floor(random() * this.options.characters.length)
        ] ?? '*',
      x: random() * width,
      y: anywhere ? random() * height : -this.options.fontSize,
      sizeMultiplier:
        1 - PER_FLAKE_SIZE_RANGE / 2 + random() * PER_FLAKE_SIZE_RANGE,
      opacityMultiplier:
        1 - PER_FLAKE_OPACITY_RANGE / 2 + random() * PER_FLAKE_OPACITY_RANGE,
      speedMultiplier: 0.7 + random() * 0.6,
      driftMultiplier: 0.5 + random(),
      phase: random() * Math.PI * 2,
      rotation: random() * 360,
      rotationSpeedMultiplier: 0.5 + random(),
    }
  }

  private step(deltaSeconds: number) {
    const { fallSpeed, wind, drift, driftFrequency, rotationSpeed } =
      this.options
    const { width, height } = this.cssSize
    const slowdown = this.hovered ? this.options.hoverSlowdown : 1
    const time = performance.now() / 1000

    this.flakes = this.flakes.map((flake) => {
      const sway =
        Math.sin(time * driftFrequency + flake.phase) *
        drift *
        flake.driftMultiplier
      const vx = (wind + sway) * flake.speedMultiplier * slowdown
      const vy = fallSpeed * flake.speedMultiplier * slowdown

      let x = flake.x + vx * deltaSeconds
      let y = flake.y + vy * deltaSeconds

      // Wrap around so flakes recycle instead of accumulating.
      const margin = this.options.fontSize
      if (y > height + margin) {
        y = -margin
      }
      if (x > width + margin) {
        x = -margin
      } else if (x < -margin) {
        x = width + margin
      }

      return {
        ...flake,
        x,
        y,
        rotation:
          (flake.rotation +
            rotationSpeed *
              flake.rotationSpeedMultiplier *
              slowdown *
              deltaSeconds) %
          360,
      }
    })
  }

  private render() {
    const { ctx } = this
    if (!ctx) return

    const { width, height } = this.cssSize
    ctx.clearRect(0, 0, width, height)

    this.drawFlakes()
  }

  private renderStaticFrame() {
    // Reduced motion: keep one scattered, non-moving frame.
    this.render()
  }

  private drawFlakes() {
    const { ctx } = this

    if (!ctx) return

    const { fontSize, fontFamily, color, opacity } = this.options

    for (const flake of this.flakes) {
      ctx.save()
      ctx.translate(flake.x, flake.y)
      ctx.rotate((flake.rotation * Math.PI) / 180)
      ctx.font = `${Math.round(fontSize * flake.sizeMultiplier)}px ${fontFamily}`
      ctx.fillStyle = color
      ctx.globalAlpha = opacity * flake.opacityMultiplier
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(flake.character, 0, 0)
      ctx.restore()
    }
  }
}
